import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, getUploadUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';

type Product = {
  _id: string;
  name: string;
  title?: string;
  image?: string;
  images?: string[];
  amountAndWeight?: string;
  price?: number;
  discountedPrice?: number;
  categoryId: string;
  categoryName?: string;
};

export function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAuth();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [related, setRelated] = useState<Product[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  useEffect(() => {
    if (!id) {
      setError('Invalid product');
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(API.productById(id));
        if (cancelled) return;
        if (res.data.success) setProduct(res.data.data);
        else setError(res.data.message || 'Product not found');
      } catch (e: unknown) {
        if (!cancelled) setError(axios.isAxiosError(e) ? (e.response?.data?.message as string) || 'Product not found' : 'Product not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProduct();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => setSelectedImageIndex(0), [product?._id]);

  // Load a few other products from the same category
  useEffect(() => {
    if (!product || !product.categoryId) {
      setRelated([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingRelated(true);
        const res = await axios.get(
          `${API.products}?categoryId=${encodeURIComponent(product.categoryId)}`
        );
        if (cancelled) return;
        if (res.data?.success && Array.isArray(res.data.data)) {
          const items: Product[] = res.data.data;
          const currentBase =
            product.discountedPrice != null && Number(product.discountedPrice) > 0
              ? Number(product.discountedPrice)
              : product.price != null
                ? Number(product.price)
                : null;
          const others = items.filter((p) => p._id !== product._id);
          const scored = currentBase == null
            ? others
            : [...others].sort((a, b) => {
                const pa =
                  a.discountedPrice != null && Number(a.discountedPrice) > 0
                    ? Number(a.discountedPrice)
                    : a.price != null
                      ? Number(a.price)
                      : NaN;
                const pb =
                  b.discountedPrice != null && Number(b.discountedPrice) > 0
                    ? Number(b.discountedPrice)
                    : b.price != null
                      ? Number(b.price)
                      : NaN;
                const da = Number.isNaN(pa) ? Number.POSITIVE_INFINITY : Math.abs(pa - currentBase);
                const db = Number.isNaN(pb) ? Number.POSITIVE_INFINITY : Math.abs(pb - currentBase);
                return da - db;
              });
          setRelated(scored.slice(0, 5));
        } else {
          setRelated([]);
        }
      } catch {
        if (!cancelled) setRelated([]);
      } finally {
        if (!cancelled) setLoadingRelated(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [product]);

  if (loading) {
    return (
      <div className="bg-slate-50/30 min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Loading product…</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="bg-slate-50/30 min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-slate-600 font-medium mb-2">{error || 'Product not found'}</p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to products
          </Link>
        </div>
      </div>
    );
  }

  const productImages = product.images?.length ? product.images : (product.image ? [product.image] : []);
  const mainImageUrl = productImages[selectedImageIndex] ? getUploadUrl(productImages[selectedImageIndex]) : null;

  return (
    <div className="bg-white min-h-[60vh]">
      <div className="container mx-auto px-4 py-6 sm:py-10 max-w-5xl">
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium text-sm mb-6 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to products
        </Link>

        <article className="flex flex-col md:flex-row md:items-stretch gap-6 md:gap-10">
          {/* Left: main image + thumbnails (multi-image gallery) */}
          <div className="w-full md:w-2/5 md:min-w-[280px] md:max-w-[400px] flex flex-col gap-3 flex-shrink-0">
            <div className="aspect-square w-full rounded-lg overflow-hidden">
              {mainImageUrl ? (
                <img
                  src={mainImageUrl}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">
                  No image
                </div>
              )}
            </div>
            {productImages.length > 1 && (
              <div className="flex flex-wrap gap-2 justify-center">
                {productImages.map((path, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedImageIndex(i)}
                    className={`w-14 h-14 rounded-lg overflow-hidden border-2 flex-shrink-0 transition ${
                      selectedImageIndex === i ? 'border-emerald-600 ring-2 ring-emerald-600/30' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <img
                      src={getUploadUrl(path)}
                      alt=""
                      className="w-full h-full object-contain bg-slate-50"
                      aria-hidden
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: product info (client-recommended layout) */}
          <div className="flex-1 min-w-0 flex flex-col">
            <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              {product.name}
            </h1>
            {product.amountAndWeight && (
              <p className="mt-2 text-slate-600 text-sm">
                Pack Size: {product.amountAndWeight}
              </p>
            )}

            {/* Green divider + Sign In / Register (when not logged in) or Price (when logged in) */}
            <div className="mt-6 pt-6 border-t border-emerald-600/30">
              {!isAuthenticated ? (
                <>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/login"
                      className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-emerald-600 text-white font-medium text-sm hover:bg-emerald-700 transition"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-emerald-600 text-white font-medium text-sm hover:bg-emerald-700 transition"
                    >
                      Register
                    </Link>
                  </div>
                  <div className="mt-4 border-t border-emerald-600/30 pt-4" />
                </>
              ) : (
                <div>
                  {product.discountedPrice != null && Number(product.discountedPrice) > 0 ? (
                    <div className="flex flex-wrap items-baseline gap-3">
                      <span className="text-2xl font-bold text-emerald-600">
                        £{Number(product.discountedPrice).toFixed(2)}
                      </span>
                      {product.price != null && (
                        <span className="text-slate-400 text-lg line-through">
                          £{Number(product.price).toFixed(2)}
                        </span>
                      )}
                    </div>
                  ) : product.price != null ? (
                    <p className="text-2xl font-bold text-emerald-600">
                      £{Number(product.price).toFixed(2)}
                    </p>
                  ) : (
                    <p className="text-slate-500 text-sm">No price set</p>
                  )}
                </div>
              )}
            </div>

            {/* Description */}
            {product.title && (
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h2 className="text-slate-800 font-semibold text-sm uppercase tracking-wide mb-2">
                  Description
                </h2>
                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap text-sm">
                  {product.title}
                </p>
              </div>
            )}
          </div>
        </article>
        {/* Related products from same category */}
        {loadingRelated ? null : related.length > 0 ? (
          <section className="mt-10 pt-8 border-t border-slate-200">
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-4">
              More in {product.categoryName || 'this category'}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5">
              {related.map((p) => {
                const imagePath = p.images?.length ? p.images[0] : p.image;
                const showDiscount =
                  p.discountedPrice != null && Number(p.discountedPrice) > 0;
                return (
                  <Link
                    key={p._id}
                    to={`/products/${p._id}`}
                    className="rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-md hover:border-slate-300 transition"
                  >
                    <div className="aspect-square w-full bg-slate-100 overflow-hidden">
                      {imagePath ? (
                        <img
                          src={getUploadUrl(imagePath)}
                          alt={p.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center text-slate-400 text-xs"
                          aria-hidden
                        >
                          No image
                        </div>
                      )}
                    </div>
                    <div className="p-3 sm:p-4">
                      <p className="font-medium text-slate-900 text-sm sm:text-base truncate">
                        {p.name}
                      </p>
                      {p.amountAndWeight && (
                        <p className="mt-1 text-xs text-slate-500">
                          {p.amountAndWeight}
                        </p>
                      )}
                      {isAuthenticated && (
                        <div className="mt-2 flex flex-wrap items-baseline gap-1.5">
                          {showDiscount ? (
                            <>
                              <span className="text-sm font-semibold text-emerald-600">
                                £{Number(p.discountedPrice).toFixed(2)}
                              </span>
                              {p.price != null && (
                                <span className="text-xs text-slate-400 line-through">
                                  £{Number(p.price).toFixed(2)}
                                </span>
                              )}
                            </>
                          ) : p.price != null ? (
                            <span className="text-sm font-semibold text-emerald-600">
                              £{Number(p.price).toFixed(2)}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
